/**
 * Seller-flow hooks (Macro #29 Stage 3 — partial LAND sale).
 * All authority is backend: my-sellable + validate-partial + demo create.
 * In PREVIEW these read/write ONLY the isolated demo substrate (gated by the
 * backend on PREVIEW_MARKETPLACE_DEMO); production stays fail-closed.
 */
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  marketplaceApi,
  type CreateWholeParcelListingBody,
  type DemoPartialListingBody,
} from "../../lib/marketplace-api";

/** Canonical row-major signature of a cell set (stable query key). */
export function cellsSig(cells: [number, number][]): string {
  return [...cells]
    .sort((a, b) => a[1] - b[1] || a[0] - b[0])
    .map(([x, y]) => `${x},${y}`)
    .join("|");
}

export function useSellableLand(enabled: boolean) {
  return useQuery({
    queryKey: ["mkt", "sellable"],
    queryFn: ({ signal }) => marketplaceApi.mySellableLand(signal),
    enabled,
    retry: false,
    staleTime: 5_000,
  });
}

/** Canonical LOCKED/ineligible cells of a parcel (seller area-selection grid). */
export function useParcelOccupancy(parcelId: number | null, enabled: boolean) {
  return useQuery({
    queryKey: ["mkt", "occupancy", parcelId],
    queryFn: ({ signal }) => marketplaceApi.parcelOccupancy(parcelId as number, signal),
    enabled: enabled && parcelId != null,
    retry: false,
    staleTime: 5_000,
  });
}

/** Debounced backend validation of the current selection. */
export function useValidatePartial(parcelId: number | null, cells: [number, number][]) {
  const sig = cellsSig(cells);
  const [debouncedSig, setDebouncedSig] = useState(sig);
  useEffect(() => {
    const tmo = setTimeout(() => setDebouncedSig(sig), 280);
    return () => clearTimeout(tmo);
  }, [sig]);

  const debouncedCells: [number, number][] = debouncedSig
    ? debouncedSig.split("|").map((s) => {
        const [x, y] = s.split(",").map(Number);
        return [x, y] as [number, number];
      })
    : [];

  return useQuery({
    queryKey: ["mkt", "validate-partial", parcelId, debouncedSig],
    enabled: parcelId != null && debouncedCells.length > 0,
    retry: false,
    staleTime: 2_000,
    queryFn: () =>
      marketplaceApi.validatePartial({
        parcel_id: parcelId as number,
        rects: debouncedCells.map(([x, y]) => ({ x, y, w: 1, h: 1 })),
      }),
  });
}

export function useCreateDemoListing() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: DemoPartialListingBody) => marketplaceApi.createDemoPartialListing(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["mkt", "mine"] });
      qc.invalidateQueries({ queryKey: ["mkt", "listings"] });
      qc.invalidateQueries({ queryKey: ["mkt", "sellable"] });
    },
  });
}

/** REAL whole-parcel listing (seller-signed EIP-712 order → POST /listings). */
export function useCreateWholeParcelListing() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateWholeParcelListingBody) => marketplaceApi.createListing(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["mkt", "mine"] });
      qc.invalidateQueries({ queryKey: ["mkt", "listings"] });
      qc.invalidateQueries({ queryKey: ["mkt", "sellable"] });
    },
  });
}
